import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, publicBaseQuery } from '@core/api/baseQuery';
import {
  preferredOrganizationUuid,
  setActiveOrganization,
} from '@features/organization/store/organizationSlice';
import { logout, setCredentials, updateUser } from '../store/authSlice';
import type {
  AcceptInvitationRequest,
  ChangePasswordRequest,
  InvitationPreview,
  LoginRequest,
  LoginResponse,
  LoginResult,
  MeResponse,
  ProfileUpdateRequest,
} from '../types';
import { isVerificationSent } from '../types';

/**
 * The endpoints that must never carry an `Authorization` header.
 *
 * `login` is the original reason: a stale token from a previous session would be
 * sent alongside the credentials, and the resulting 401 would be misread by the
 * authenticated base query as "the current session expired", logging the user out
 * mid-login. The two invitation endpoints are public for the same reason and one
 * more: whoever opens the link may be signed in as somebody else entirely, and the
 * link belongs to the address it was sent to, not to the browser it is opened in.
 * The emailed sign-in link (`verifyLogin`) is the same kind of link.
 */
const PUBLIC_ENDPOINTS = new Set(['login', 'verifyLogin', 'previewInvitation', 'acceptInvitation']);

/** Endpoint-aware base query: public where it has to be, authenticated everywhere else. */
const authBaseQuery: typeof baseQuery = (args, api, extraOptions) =>
  PUBLIC_ENDPOINTS.has(api.endpoint)
    ? // Both are base queries over the same fetch; only the error shape differs
      // (raw `FetchBaseQueryError` vs. the normalized `ApiError`). The value is
      // passed through untouched — `apiErrorMessage` reads either.
      (publicBaseQuery(args, api, extraOptions) as unknown as ReturnType<typeof baseQuery>)
    : baseQuery(args, api, extraOptions);

/**
 * Start a session from a login-shaped payload.
 *
 * Order matters: the store's `setCredentials` listener wipes the previous user's
 * tenant state (the active organization included), so the selection has to be
 * dispatched after it. Accepting an invitation ends in exactly the same state as
 * logging in, so it goes through the same two steps.
 */
function startSession(dispatch: (action: unknown) => unknown, data: LoginResponse) {
  const organizations = data.organizations ?? [];
  dispatch(setCredentials({ user: data.user, token: data.token, organizations }));
  const active = preferredOrganizationUuid(organizations, data.user.default_organization_uuid);
  if (active) {
    dispatch(setActiveOrganization(active));
  }
}

/**
 * Auth API using RTK Query.
 *
 * User-scoped (never tenant-scoped), so its cache is NOT reset when the active
 * organization changes.
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: authBaseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<LoginResult, LoginRequest>({
      query: (credentials) => ({
        url: '/api/auth/login/',
        method: 'POST',
        body: credentials,
      }),
      /**
       * The usual place a session starts (see also `verifyLogin` and
       * `acceptInvitation`). With the emailed second step on, the answer is only the
       * notice that the link was sent, and the session starts from the link.
       */
      async onQueryStarted(_credentials, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!isVerificationSent(data)) {
            startSession(dispatch, data);
          }
        } catch {
          // Bad credentials / network failure: the form surfaces the rejection.
        }
      },
    }),

    /**
     * Open the session from the emailed sign-in link (ADR-0022). `404` for a link
     * that never existed, `410` with `code: used | expired` for one that can no
     * longer be used.
     */
    verifyLogin: builder.mutation<LoginResponse, string>({
      query: (token) => ({ url: '/api/auth/login/verify/', method: 'POST', body: { token } }),
      async onQueryStarted(_token, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          startSession(dispatch, data);
        } catch {
          // A dead link: the screen surfaces the rejection.
        }
      },
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/api/auth/logout/',
        method: 'POST',
      }),
      /**
       * Deleting the token server-side is best effort: an already expired or
       * rotated token answers 401, and the local session must go either way.
       */
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // Ignored on purpose — see above.
        }
        dispatch(logout());
      },
    }),
    /**
     * `GET /api/auth/me/` returns `{ user, organizations, memberships }` — the
     * caller wrapped alongside their tenancy context, NOT a bare user object.
     */
    me: builder.query<MeResponse, void>({
      query: () => ({
        url: '/api/auth/me/',
        method: 'GET',
      }),
    }),

    /**
     * The name a person gives themselves. The email is the login and the tenancy
     * key, so only the Kura team changes it.
     */
    updateProfile: builder.mutation<MeResponse, ProfileUpdateRequest>({
      query: (body) => ({ url: '/api/auth/me/', method: 'PATCH', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        // The header reads the name off the session, so it has to hear about this.
        dispatch(updateUser(data.user));
      },
    }),

    /** A new password, proved by the current one. The session is kept. */
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (body) => ({ url: '/api/auth/password/', method: 'POST', body }),
    }),

    /**
     * What an invitation link says, before anybody types anything into it.
     *
     * `404` for a link that never existed or was revoked, `410` with
     * `code: accepted | expired` for one that can no longer be used. The screen
     * tells those apart because the answer differs: sign in, or ask for a new link.
     */
    previewInvitation: builder.query<InvitationPreview, string>({
      query: (token) => ({ url: `/api/auth/invitations/${token}/`, method: 'GET' }),
    }),

    /** Accept the invitation and land signed in, exactly as a login would. */
    acceptInvitation: builder.mutation<LoginResponse, AcceptInvitationRequest>({
      query: ({ token, ...body }) => ({
        url: `/api/auth/invitations/${token}/accept/`,
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          startSession(dispatch, data);
        } catch {
          // A wrong password or a dead link: the screen surfaces the rejection.
        }
      },
    }),
  }),
});

// Only the hooks something actually calls are exported. `me` keeps its endpoint
// definition — it mirrors a real backend route and its response type is pinned by
// `core/types/__tests__/apiContract.test.ts` — and is reachable as
// `authApi.endpoints.me` until a screen needs it.
export const {
  useLoginMutation,
  useVerifyLoginMutation,
  useLogoutMutation,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  usePreviewInvitationQuery,
  useAcceptInvitationMutation,
} = authApi;
